import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Scene } from '@/model';
import { clickFromKeyboard } from '@/util/keyboardUtil';
import { useScrollControl } from '@/hook/useScrollControl';
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from '@/component/Popover';
import { VirtualList, VirtualListRef } from '@/component/VirtualList';
import { DeleteIcon, PasteIcon, RefreshIcon, HomeIcon } from '@/assets/svgs/icons';


interface SceneListProps {
    scenes: Scene[];
    selectedId?: number;
    modifiedScenes: { [key: number]: Scene };
    invalidSceneIds: number[];
    welcomeSceneId?: number;
    fallbackSceneId?: number;
    onClickItem: (sceneId: number) => void;
    onChangeWelcome: (sceneId: number) => Promise<void>;
    onChangeFallback: (sceneId: number) => Promise<void>;
    onDuplicate: (sceneId: number) => Promise<void>;
    onDelete: (sceneId: number) => Promise<void>;
    isSubmitting: boolean;
}

const SceneList: React.FC<SceneListProps> = (props) => {
    const {
        scenes,
        selectedId,
        modifiedScenes,
        invalidSceneIds,
        welcomeSceneId,
        fallbackSceneId,
        onClickItem,
        onChangeWelcome,
        onChangeFallback,
        onDuplicate,
        onDelete,
        isSubmitting
    } = props;

    const intl = useIntl();

    const virtualListRef = useRef<VirtualListRef>(null);
    const { setAutoScrollId, scrollTo } = useScrollControl(scenes, virtualListRef);

    useEffect(() => {
        scrollTo(selectedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const renderSceneItem = useCallback((scene: Scene) => {
        const isSelected = selectedId === scene.id;
        const isInvalid = invalidSceneIds.includes(scene.id);
        const isFallback = fallbackSceneId === scene.id;
        const isWelcome = welcomeSceneId === scene.id;
        const modifiedScene = modifiedScenes[scene.id];
        const sceneData = modifiedScene ?? scene;

        return (
            <div role="listitem"
                 tabIndex={0}
                 aria-current={isSelected}
                 aria-label={`${sceneData.name}, ${sceneData.custom_id}`}
                 className={classNames(
                     "scene-item",
                     modifiedScene && "modified",
                     isSelected && "selected",
                     isInvalid && "invalid"
                 )}
                 onClick={() => {
                     setAutoScrollId(scene.id);
                     onClickItem(scene.id);
                 }}
                 onKeyDown={clickFromKeyboard}
            >
                <div className="flex items-center gap-2">
                    <div className="scene-item-label"
                         data-testid="scene-item-label">
                        {sceneData.custom_id}
                    </div>
                    {isWelcome && (
                        <span role="status"
                              aria-label={intl.formatMessage({ id: "i02008" })}
                              className="badge-info"
                        >
                            {intl.formatMessage({ id: "i02008" })}
                        </span>
                    )}
                    {isFallback && (
                        <span role="status"
                              aria-label={intl.formatMessage({ id: "i02009" })}
                              className="badge-danger"
                        >
                            {intl.formatMessage({ id: "i02009" })}
                        </span>
                    )}
                </div>
                <div className="flex justify-between items-center">
                    <div className="text-[14px] font-[500] text-black-500">
                        {sceneData.name}
                    </div>
                    <Popover className="flex-none">
                        {({ open, close }) => (
                            <>
                                <PopoverButton open={open}
                                               close={close}
                                />
                                <PopoverMenu anchor="right start">
                                    {!isWelcome && !isFallback && (<>
                                        <PopoverItem className="scene-popover-item"
                                                     disabled={isSubmitting}
                                                     onClick={() => onChangeWelcome(scene.id)}
                                        >
                                            <HomeIcon className="size-4" />
                                            {intl.formatMessage({ id: "i02010" })}
                                        </PopoverItem>
    
                                        <PopoverItem className="scene-popover-item"
                                                     disabled={isSubmitting}
                                                     onClick={() => onChangeFallback(scene.id)}
                                        >
                                            <RefreshIcon className="size-4" />
                                            {intl.formatMessage({ id: "i02011" })}
                                        </PopoverItem>
                                    </>)}
                                    <PopoverItem className="scene-popover-item"
                                                 disabled={isSubmitting}
                                                 onClick={() => onDuplicate(scene.id)}
                                    >
                                        <PasteIcon className="size-4" />
                                        {intl.formatMessage({ id: "i02021" })}
                                    </PopoverItem>
                                    <PopoverItem className="scene-popover-item"
                                                 disabled={isSubmitting}
                                                 onClick={() => onDelete(scene.id)}
                                    >
                                        <DeleteIcon className="size-3.5 text-point-red" />
                                        {intl.formatMessage({ id: "i02012" })}
                                    </PopoverItem>
                                </PopoverMenu>
                            </>
                        )}
                    </Popover>
                </div>
            </div>
        );
    }, [
        modifiedScenes, selectedId, invalidSceneIds, welcomeSceneId, fallbackSceneId, isSubmitting,
        onChangeWelcome, onChangeFallback, intl, onClickItem, onDuplicate, onDelete, setAutoScrollId
    ]);

    if (scenes.length === 0) {
        return null;
    }
    return (
        <VirtualList ref={virtualListRef}
                     className="scene-list scroll-gap-container"
                     data-testid="scene-list"
                     items={scenes}
                     itemHeight={84}
                     gap={4}
                     visibleItems={10}
                     getItemKey={scene => scene.id}
                     renderItem={renderSceneItem}
        />
    );
};

export default SceneList;
